/**
 * Nolana theme — collection / search filtering without page reloads.
 * Ported in spirit from tema-demo (client-side filtering) onto Shopify's Section Rendering API:
 * every filter/sort/pagination change fetches the current results section
 * (`?section_id=…`) and swaps its content, keeping the URL in sync with history.
 *
 * Markup contract (sections/main-collection-grid.liquid, main-search.liquid):
 *   [data-collection-body][data-section-id][data-collection-url]   results container
 *   [data-facets-form] with [data-facet-input] / [data-facet-price]  inside #FilterDrawer
 *   [data-sort-select]                                             sort control (outside the form)
 *   [data-facet-link]                                              links handled via AJAX (active
 *                                                                  filter removal, clear, pagination)
 *   [data-filter-count] / [data-result-count]                      counters refreshed after render
 */
(function () {
  "use strict";

  if (document.documentElement.dataset.nolanaFacetsReady) return;
  document.documentElement.dataset.nolanaFacetsReady = "true";

  var DEBOUNCE = 350;
  var timer = null;
  var controller = null;

  function body() {
    return document.querySelector("[data-collection-body]");
  }

  function drawer() {
    return document.getElementById("FilterDrawer");
  }

  function baseUrl() {
    var element = body();
    return (element && element.getAttribute("data-collection-url")) || window.location.pathname;
  }

  function sectionId() {
    var element = body();
    return element ? element.getAttribute("data-section-id") : null;
  }

  /** Builds the results URL from the drawer form + sort select (+ preserved search terms). */
  function buildUrl() {
    var form = document.querySelector("[data-facets-form]");
    var params = new URLSearchParams();
    var sort = document.querySelector("[data-sort-select]");
    if (form) {
      var data = new FormData(form);
      data.forEach(function (value, key) {
        // The hidden sort_by input only yields to a visible sort select.
        if (key === "sort_by" && sort) return;
        if (String(value).trim() === "") return;
        params.append(key, value);
      });
    }
    if (sort && sort.value) params.set("sort_by", sort.value);
    // Search page without a filter form: keep the query and prefix option from the current URL.
    var currentParams = new URLSearchParams(window.location.search);
    ["q", "options[prefix]"].forEach(function (key) {
      if (!params.has(key) && currentParams.has(key)) params.set(key, currentParams.get(key));
    });
    var query = params.toString();
    return baseUrl() + (query ? "?" + query : "");
  }

  /** Remembers which toolbar control has focus so it can be re-focused after a swap. */
  function describeToolbarFocus() {
    var active = document.activeElement;
    if (!active) return null;
    if (active.hasAttribute("data-sort-select")) return "[data-sort-select]";
    if (active.hasAttribute("data-open-filter")) return "[data-open-filter]";
    return null;
  }

  function setLoading(loading) {
    var element = body();
    if (!element) return;
    element.classList.toggle("is-loading", loading);
    element.setAttribute("aria-busy", loading ? "true" : "false");
  }

  function syncDrawer(freshDrawer) {
    var current = drawer();
    if (!current || !freshDrawer) return;
    var currentItems = current.querySelector("[data-facets-items]");
    var freshItems = freshDrawer.querySelector("[data-facets-items]");
    var currentFoot = current.querySelector(".drawer__foot");
    var freshFoot = freshDrawer.querySelector(".drawer__foot");
    var focused = document.activeElement;
    var inDrawer = !!(focused && current.contains(focused));
    var typingPrice = inDrawer && focused.hasAttribute("data-facet-price");
    var focusName = inDrawer ? focused.getAttribute("name") : null;
    var focusValue = inDrawer && focused.type === "checkbox" ? focused.value : null;

    if (typingPrice) {
      // Keep the field the user is typing in; only refresh checkbox counts and states.
      if (currentItems && freshItems) {
        freshItems.querySelectorAll("[data-facet-input]").forEach(function (freshInput) {
          var selector = '[data-facet-input][name="' + CSS.escape(freshInput.name) + '"][value="' + CSS.escape(freshInput.value) + '"]';
          var currentInput = currentItems.querySelector(selector);
          if (!currentInput) return;
          currentInput.disabled = freshInput.disabled;
          var currentLabel = currentInput.closest(".f-check");
          var freshLabel = freshInput.closest(".f-check");
          if (currentLabel && freshLabel) {
            currentLabel.classList.toggle("is-disabled", freshLabel.classList.contains("is-disabled"));
            var currentCount = currentLabel.querySelector("small");
            var freshCount = freshLabel.querySelector("small");
            if (currentCount && freshCount) currentCount.textContent = freshCount.textContent;
          }
        });
      }
    } else if (currentItems && freshItems) {
      currentItems.innerHTML = freshItems.innerHTML;
    }
    if (currentFoot && freshFoot) currentFoot.innerHTML = freshFoot.innerHTML;

    if (focusName && !typingPrice) {
      var selector = '[name="' + CSS.escape(focusName) + '"]' + (focusValue !== null ? '[value="' + CSS.escape(focusValue) + '"]' : "");
      var target = current.querySelector(selector);
      if (target) target.focus({ preventScroll: true });
    }
  }

  function afterRender(toolbarFocus) {
    var layers = window.Nolana && window.Nolana.layers;
    var open = !!(layers && layers.isOpen("FilterDrawer"));
    document.querySelectorAll('[aria-controls="FilterDrawer"]').forEach(function (trigger) {
      trigger.setAttribute("aria-expanded", open ? "true" : "false");
    });
    if (open) {
      var element = drawer();
      if (element && window.Nolana && typeof window.Nolana.trapFocus === "function") {
        var focusTarget = element.contains(document.activeElement) ? document.activeElement : element.querySelector("[data-layer-focus]");
        window.Nolana.trapFocus(element, focusTarget);
      }
    } else if (toolbarFocus) {
      var control = document.querySelector(toolbarFocus);
      if (control) control.focus({ preventScroll: true });
    }
  }

  function render(html) {
    var doc = new DOMParser().parseFromString(html, "text/html");
    var freshBody = doc.querySelector("[data-collection-body]");
    var current = body();
    if (!freshBody || !current) return false;
    var freshDrawer = freshBody.querySelector("#FilterDrawer");
    if (freshDrawer) freshDrawer.remove();
    var toolbarFocus = describeToolbarFocus();
    current.innerHTML = freshBody.innerHTML;
    syncDrawer(freshDrawer);
    afterRender(toolbarFocus);
    // Announce the new result count from a persistent live region (a re-created one stays silent).
    var status = document.querySelector("[data-facets-status]");
    var count = current.querySelector("[data-result-count]");
    if (status && count) {
      var countText = count.textContent.trim();
      status.textContent = "";
      window.requestAnimationFrame(function () { status.textContent = countText; });
    }
    if (window.Nolana && typeof window.Nolana.initReveal === "function") window.Nolana.initReveal(current);
    document.dispatchEvent(new CustomEvent("nolana:facets:rendered", { detail: { url: window.location.href } }));
    return true;
  }

  function load(url, options) {
    options = options || {};
    var id = sectionId();
    if (!id) {
      window.location.href = url;
      return;
    }
    if (controller) controller.abort();
    controller = new AbortController();
    setLoading(true);
    var own = controller;
    var fetchUrl = url + (url.indexOf("?") > -1 ? "&" : "?") + "section_id=" + encodeURIComponent(id);
    fetch(fetchUrl, { signal: own.signal })
      .then(function (response) {
        if (!response.ok) throw new Error("facets " + response.status);
        return response.text();
      })
      .then(function (html) {
        if (!render(html)) throw new Error("facets render");
        if (options.push !== false) history.pushState({ nolanaFacets: true }, "", url);
        if (options.scroll) {
          var toolbar = document.querySelector("[data-collection-toolbar]") || body();
          var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
          if (toolbar) toolbar.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
        }
      })
      .catch(function (error) {
        if (error && error.name === "AbortError") return;
        window.location.href = url;
      })
      .finally(function () {
        // An aborted request must not clear the loading state of its replacement.
        if (own === controller) setLoading(false);
      });
  }

  function scheduleUpdate() {
    clearTimeout(timer);
    timer = setTimeout(function () {
      load(buildUrl());
    }, DEBOUNCE);
  }

  /* ---------- Events ---------- */
  document.addEventListener("change", function (event) {
    if (event.target.closest("[data-facet-input], [data-facet-price]")) {
      scheduleUpdate();
      return;
    }
    if (event.target.closest("[data-sort-select]")) {
      load(buildUrl());
    }
  });

  document.addEventListener("submit", function (event) {
    var form = event.target.closest("[data-facets-form]");
    if (!form) return;
    event.preventDefault();
    clearTimeout(timer);
    load(buildUrl());
  });

  document.addEventListener("click", function (event) {
    var link = event.target.closest("[data-facet-link]");
    if (!link || !body()) return;
    if (link.getAttribute("aria-disabled") === "true") {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    var isPagination = !!link.closest(".pagination");
    load(link.getAttribute("href"), { scroll: isPagination });
  });

  window.addEventListener("popstate", function () {
    if (body()) load(window.location.pathname + window.location.search, { push: false });
  });

  window.Nolana = window.Nolana || {};
  window.Nolana.facets = { load: load, buildUrl: buildUrl };
})();
